<template>
  <div>
    <Teleport to="body">
      <div
        v-if="imageIndex !== null"
        class="vel-modal vel-modal--ecommerce"
        @click.self="imageIndex = null"
      >
        <div class="vel-lightbox-frame">
          <button
            class="vel-ctrl-btn vel-ctrl-btn--close"
            aria-label="Close"
            @click="imageIndex = null"
          >
            <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="white"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
          </button>
          <div class="vel-lightbox-content">
            <div class="vel-lightbox-media">
              <img
                ref="lightboxImage"
                :src="items[imageIndex || 0]?.image?.large || ''"
                :alt="items[imageIndex || 0]?.title"
                class="vel-img"
                @load="updateLightboxTitleWidth"
              />
              <p
                v-if="items[imageIndex || 0]?.title"
                class="vel-lightbox-title"
                :style="lightboxTitleStyle"
              >
                {{ items[imageIndex || 0]?.title }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
    <div
      :class="{
        'tw-flex tw-justify-center': items.length === 0,
        'tw-grid tw-grid-cols-3 sm:tw-grid-cols-4 tw-gap-2 md:tw-gap-4':
          items.length > 0,
      }"
    >
      <MediaImageLazyLoad
        v-for="(item, index) in items"
        :key="index"
        :src="item.image?.large || ''"
        :blank-src="item.image?.tiny || ''"
        :alt="item.title"
        :ratio="1"
        class="hover:tw-cursor-pointer"
        custom-class="tw-rounded-lg"
        @click="onImageClick(index, item.title)"
      />
      <LandingNoData v-if="items.length === 0" />
    </div>
  </div>
</template>

<script setup lang="ts">
interface Props {
  items: {
    image: ImageMeta;
    title: string;
    description?: string;
  }[];
  actionEventName?: string;
  fbEventName?: string;
  eventName?: string;
}

const props = withDefaults(defineProps<Props>(), {
  actionEventName: "",
  fbEventName: "",
  eventName: "",
});

const imageIndex = ref<number | null>(null);
const lightboxImage = ref<HTMLImageElement | null>(null);
const lightboxImageWidth = ref<number | null>(null);

const { analyticEvent, fbPixelEvent } = useAnalytic();
const onImageClick = (index: number, title: string) => {
  imageIndex.value = index;
  analyticEvent({
    ga4ActionName: `click_gallery_${props.eventName}`,
    ga4Label: title,
  });
  fbPixelEvent({
    fbCategory: "trackCustom",
    fbEvent: props.fbEventName,
    fbValue: { label: title },
  });
};

const updateLightboxTitleWidth = () => {
  lightboxImageWidth.value = lightboxImage.value?.clientWidth || null;
};

const lightboxTitleStyle = computed(() =>
  lightboxImageWidth.value
    ? { width: `${lightboxImageWidth.value}px`, maxWidth: "100%" }
    : { maxWidth: "100%" },
);

watch(imageIndex, async (value) => {
  if (value === null) {
    lightboxImageWidth.value = null;
    return;
  }

  await nextTick();
  updateLightboxTitleWidth();
});

onMounted(() => {
  window.addEventListener("resize", updateLightboxTitleWidth);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateLightboxTitleWidth);
});
</script>

<style scoped>
.vel-lightbox-frame {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  max-width: 100%;
}

.vel-lightbox-content {
  display: flex;
  align-items: center;
  max-width: 100%;
}

.vel-lightbox-media {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  width: fit-content;
  max-width: 100%;
}

.vel-lightbox-title {
  margin-top: 16px;
  color: white;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.5;
  text-align: left;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.vel-img {
  display: block;
  margin: 0 auto;
  max-width: 100%;
  border-radius: 8px;
}

.vel-ctrl-btn--close {
  position: absolute;
  top: 0;
  right: 0;
  transform: translateY(calc(-100% - 8px));
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  min-width: 0;
  min-height: 0;
  z-index: 9999;
  background: transparent;
  padding: 0;
  line-height: 0;
}

@media (max-width: 750px) {
  .vel-modal--ecommerce {
    flex-direction: row;
    align-items: center;
    justify-content: center;
    padding: 16px;
  }

  .vel-lightbox-frame,
  .vel-lightbox-content {
    max-width: 90vw;
  }
}
</style>
